/* ==========================================================================
   Lumen — theme.js (component core)
   Framework-free. Custom elements + delegation. Everything is progressive
   enhancement: the theme is fully usable with JS disabled.
   Public API: window.theme = { utils, toast, cart (from cart.js) }
   ========================================================================== */
(function () {
  'use strict';

  window.theme = window.theme || {};
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ----------------------------------------------------------------------
     Utils
  ---------------------------------------------------------------------- */
  theme.utils = {
    debounce: function (fn, ms) {
      var t;
      return function () {
        var args = arguments, self = this;
        clearTimeout(t);
        t = setTimeout(function () { fn.apply(self, args); }, ms);
      };
    },
    fetchConfig: function (type) {
      return {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': type || 'application/json' }
      };
    },
    onEscape: function (fn) {
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape') fn(e); });
    }
  };

  /* ----------------------------------------------------------------------
     Toast
  ---------------------------------------------------------------------- */
  theme.toast = function (message, opts) {
    opts = opts || {};
    var region = document.querySelector('.toast-region');
    if (!region) {
      region = document.createElement('div');
      region.className = 'toast-region';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      document.body.appendChild(region);
    }
    var el = document.createElement('div');
    el.className = 'toast';
    el.textContent = message;
    region.appendChild(el);
    setTimeout(function () { el.remove(); }, opts.duration || 3200);
  };

  /* ----------------------------------------------------------------------
     <lumen-drawer> / <lumen-modal> — wrap a native <dialog>
     Triggers: [data-drawer-trigger="id"] / [data-modal-trigger="id"]
  ---------------------------------------------------------------------- */
  function dialogFactory(tag, closeAnimated) {
    if (customElements.get(tag)) return;
    customElements.define(tag, class extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('dialog');
        if (!this.dialog) return;
        var self = this;
        this.dialog.addEventListener('click', function (e) {
          // click on backdrop = click directly on <dialog>
          if (e.target === self.dialog) self.close();
        });
        this.querySelectorAll('[data-dialog-close]').forEach(function (btn) {
          btn.addEventListener('click', function () { self.close(); });
        });
      }
      open() {
        if (!this.dialog || this.dialog.open) return;
        this.dialog.showModal();
        document.body.style.overflow = 'hidden';
        this.dispatchEvent(new CustomEvent('dialog:open', { bubbles: true }));
      }
      close() {
        if (!this.dialog || !this.dialog.open) return;
        var self = this;
        var done = function () {
          self.dialog.close();
          document.body.style.overflow = '';
          self.dispatchEvent(new CustomEvent('dialog:close', { bubbles: true }));
        };
        if (closeAnimated && !reducedMotion) {
          // let the CSS transition play out before closing
          this.dialog.removeAttribute('open-anim');
          setTimeout(done, 250);
        } else {
          done();
        }
      }
    });
  }
  dialogFactory('lumen-drawer', true);
  dialogFactory('lumen-modal', true);

  document.addEventListener('click', function (e) {
    var t = e.target.closest('[data-drawer-trigger], [data-modal-trigger]');
    if (!t) return;
    var id = t.getAttribute('data-drawer-trigger') || t.getAttribute('data-modal-trigger');
    var host = document.getElementById(id);
    if (host && typeof host.open === 'function') {
      e.preventDefault();
      host.open();
    }
  });

  /* ----------------------------------------------------------------------
     <lumen-slider> — scroll-snap carousel
     Children: [data-slider-track] with slides. Attrs: data-arrows,
     data-dots, data-autoplay="5000"
  ---------------------------------------------------------------------- */
  if (!customElements.get('lumen-slider')) {
    customElements.define('lumen-slider', class extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-slider-track]');
        if (!this.track) return;
        this.slides = Array.prototype.slice.call(this.track.children);
        if (this.slides.length < 2) return;

        if (this.hasAttribute('data-arrows')) this._arrows();
        if (this.hasAttribute('data-dots')) this._dots();
        if (this.hasAttribute('data-progress')) this._progress();
        this._dragScroll();

        var self = this;
        this.track.addEventListener('scroll', theme.utils.debounce(function () { self._sync(); }, 80), { passive: true });

        var autoplay = parseInt(this.getAttribute('data-autoplay'), 10);
        if (autoplay && !reducedMotion) {
          this._timer = setInterval(function () { self.next(true); }, autoplay);
          ['mouseenter', 'focusin', 'touchstart'].forEach(function (ev) {
            self.addEventListener(ev, function () { clearInterval(self._timer); }, { once: true, passive: true });
          });
        }
        this._sync();
      }
      disconnectedCallback() {
        clearInterval(this._timer);
        if (this._arrowRO) { this._arrowRO.disconnect(); this._arrowRO = null; }
      }
      _index() {
        var x = this.track.scrollLeft, best = 0, dist = Infinity;
        this.slides.forEach(function (s, i) {
          var d = Math.abs(s.offsetLeft - x);
          if (d < dist) { dist = d; best = i; }
        });
        return best;
      }
      goTo(i) {
        i = Math.max(0, Math.min(i, this.slides.length - 1));
        this.track.scrollTo({ left: this.slides[i].offsetLeft, behavior: reducedMotion ? 'auto' : 'smooth' });
      }
      next(loop) {
        var i = this._index() + 1;
        if (i >= this.slides.length) { if (loop) i = 0; else return; }
        this.goTo(i);
      }
      prev() { this.goTo(this._index() - 1); }
      _arrows() {
        var self = this;
        ['prev', 'next'].forEach(function (dir) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'slider__arrow slider__arrow--' + dir;
          b.setAttribute('aria-label', dir === 'prev' ? 'Previous slide' : 'Next slide');
          b.innerHTML = dir === 'prev'
            ? '<svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6"/></svg>'
            : '<svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6"/></svg>';
          b.addEventListener('click', function () { dir === 'prev' ? self.prev() : self.next(); });
          self.appendChild(b);
        });
        this.prevBtn = this.querySelector('.slider__arrow--prev');
        this.nextBtn = this.querySelector('.slider__arrow--next');
        this._alignArrows();
      }
      /* Centre the arrows on the slide's image rather than on the slide box.
         A product card is image + title + price + description, so centring on
         the box drops the arrows well below the middle of the picture. Measured
         rather than derived from the aspect ratio, because slides differ per
         section. Observed so it re-runs when the image loads or the card
         reflows; falls back to the CSS 50% if there is nothing to measure. */
      _alignArrows() {
        var first = this.slides[0];
        if (!first) return;
        /* Only a real media box counts. Matching any img/svg would pick up the
           first icon in the slide — on a review card that is a rating star, so
           the arrows ended up level with the stars instead of the card.
           Slides with no media box (reviews, testimonials, plain text) centre on
           the track, which is the slides themselves; the plain CSS 50% would
           include the dots underneath and sit slightly low. */
        var media = first.querySelector('.product-card__media, .card__media, [class$="__media"]')
          || this.track;
        if (!media) return;
        var self = this;
        var apply = function () {
          var m = media.getBoundingClientRect();
          if (!m.height) return;
          var y = m.top - self.getBoundingClientRect().top + m.height / 2;
          self.style.setProperty('--slider-arrow-y', Math.round(y) + 'px');
        };
        /* Measured from several angles on purpose. Whether the card has a height
           yet when the element upgrades depends on how far the stylesheet and
           the images have got, and that order is not guaranteed — measuring only
           once at connect left the arrows on the CSS 50% fallback. */
        apply();
        requestAnimationFrame(apply);
        if (document.readyState !== 'complete') window.addEventListener('load', apply, { once: true });
        if (typeof ResizeObserver === 'function') {
          this._arrowRO = new ResizeObserver(apply);
          this._arrowRO.observe(media);
        }
      }
      _dots() {
        var self = this;
        var wrap = document.createElement('div');
        wrap.className = 'slider__dots';
        wrap.setAttribute('role', 'tablist');
        this.slides.forEach(function (_, i) {
          var d = document.createElement('button');
          d.type = 'button';
          d.className = 'slider__dot';
          d.setAttribute('aria-label', 'Go to slide ' + (i + 1));
          d.addEventListener('click', function () { self.goTo(i); });
          wrap.appendChild(d);
        });
        this.appendChild(wrap);
        this.dots = Array.prototype.slice.call(wrap.children);
      }
      _progress() {
        var self = this;
        var wrap = document.createElement('div');
        wrap.className = 'slider__progress';
        var bar = document.createElement('div');
        bar.className = 'slider__progress-bar';
        wrap.appendChild(bar);
        this.appendChild(wrap);
        var raf = null;
        this._updateProgress = function () {
          raf = null;
          var t = self.track;
          var overflow = t.scrollWidth - t.clientWidth;
          if (overflow <= 1) { wrap.style.display = 'none'; return; }
          wrap.style.display = '';
          var frac = t.clientWidth / t.scrollWidth;   // thumb width as a fraction of the track
          var pct = t.scrollLeft / overflow;          // 0..1 scroll progress
          bar.style.width = (frac * 100) + '%';
          bar.style.transform = 'translateX(' + (pct * (1 - frac) / frac * 100) + '%)';
        };
        this.track.addEventListener('scroll', function () {
          if (raf) return;
          raf = requestAnimationFrame(self._updateProgress);
        }, { passive: true });
        window.addEventListener('resize', theme.utils.debounce(function () { self._updateProgress(); }, 150), { passive: true });
        this._updateProgress();
      }
      /* Mouse drag-to-scroll (touch already scrolls natively). Deliberately
         click-safe: no pointer capture, and a drag only "engages" once the
         pointer has moved past a threshold — so ordinary clicks (which jitter
         a few px) always pass through to links / quick-view / quick-add. Snap
         is paused only during a real drag and restored on release. */
      _dragScroll() {
        var track = this.track, self = this;
        var down = false, dragging = false, startX = 0, startLeft = 0, snap = '';
        var THRESHOLD = 10;
        track.addEventListener('pointerdown', function (e) {
          if (e.pointerType === 'touch' || e.button !== 0) return;
          down = true; dragging = false;
          startX = e.clientX; startLeft = track.scrollLeft;
        });
        window.addEventListener('pointermove', function (e) {
          if (!down) return;
          var dx = e.clientX - startX;
          if (!dragging) {
            if (Math.abs(dx) < THRESHOLD) return;   // still a click, not a drag
            dragging = true;
            snap = track.style.scrollSnapType;
            track.style.scrollSnapType = 'none';
            self.classList.add('is-grabbing');
          }
          track.scrollLeft = startLeft - dx;
          e.preventDefault();
        }, { passive: false });
        function end() {
          if (!down) return;
          down = false;
          if (dragging) {
            track.style.scrollSnapType = snap;
            self.classList.remove('is-grabbing');
          }
        }
        window.addEventListener('pointerup', end);
        window.addEventListener('pointercancel', end);
        // Only a genuine drag suppresses the click on the card beneath it.
        track.addEventListener('click', function (e) {
          if (dragging) { e.preventDefault(); e.stopPropagation(); }
        }, true);
        track.addEventListener('dragstart', function (e) { if (dragging) e.preventDefault(); });
      }
      _sync() {
        var i = this._index();
        if (this.dots) this.dots.forEach(function (d, j) {
          if (j === i) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current');
        });
        if (this.prevBtn) this.prevBtn.toggleAttribute('disabled', i === 0);
        if (this.nextBtn) this.nextBtn.toggleAttribute('disabled', i === this.slides.length - 1 && !this.getAttribute('data-autoplay'));
        if (this._updateProgress) this._updateProgress();
      }
    });
  }

  /* ----------------------------------------------------------------------
     <lumen-accordion data-single> — details enhancement
  ---------------------------------------------------------------------- */
  if (!customElements.get('lumen-accordion')) {
    customElements.define('lumen-accordion', class extends HTMLElement {
      connectedCallback() {
        if (!this.hasAttribute('data-single')) return;
        var items = this.querySelectorAll('details');
        items.forEach(function (d) {
          d.addEventListener('toggle', function () {
            if (!d.open) return;
            items.forEach(function (other) { if (other !== d) other.open = false; });
          });
        });
      }
    });
  }

  /* ----------------------------------------------------------------------
     <lumen-tabs> — accessible tabs
  ---------------------------------------------------------------------- */
  if (!customElements.get('lumen-tabs')) {
    customElements.define('lumen-tabs', class extends HTMLElement {
      connectedCallback() {
        var self = this;
        this.tabs = Array.prototype.slice.call(this.querySelectorAll('[role="tab"]'));
        this.panels = Array.prototype.slice.call(this.querySelectorAll('[role="tabpanel"]'));
        this.tabs.forEach(function (tab, i) {
          tab.addEventListener('click', function () { self.select(i); });
          tab.addEventListener('keydown', function (e) {
            var dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
            if (!dir) return;
            e.preventDefault();
            var n = (i + dir + self.tabs.length) % self.tabs.length;
            self.select(n);
            self.tabs[n].focus();
          });
        });
      }
      select(i) {
        this.tabs.forEach(function (t, j) {
          t.setAttribute('aria-selected', String(j === i));
          t.tabIndex = j === i ? 0 : -1;
        });
        this.panels.forEach(function (p, j) { p.hidden = j !== i; });
      }
    });
  }

  /* ----------------------------------------------------------------------
     <lumen-countdown data-deadline="ISO" data-end="hide|message">
     Children: [data-cd-days] [data-cd-hours] [data-cd-mins] [data-cd-secs]
     Optional: [data-cd-message] shown at end.
  ---------------------------------------------------------------------- */
  if (!customElements.get('lumen-countdown')) {
    customElements.define('lumen-countdown', class extends HTMLElement {
      connectedCallback() {
        var deadline = new Date(this.getAttribute('data-deadline'));
        if (isNaN(deadline)) return;
        var self = this;
        var els = {
          d: this.querySelector('[data-cd-days]'),
          h: this.querySelector('[data-cd-hours]'),
          m: this.querySelector('[data-cd-mins]'),
          s: this.querySelector('[data-cd-secs]')
        };
        function pad(n) { return String(n).padStart(2, '0'); }
        function setUnit(el, val) {
          if (!el) return;
          var s = pad(val);
          if (el.textContent === s) return;   // only animate the digits that changed
          el.textContent = s;
          if (reducedMotion) return;
          el.classList.remove('is-tick');
          void el.offsetWidth;                 // restart the animation
          el.classList.add('is-tick');
        }
        function tick() {
          var diff = deadline - new Date();
          if (diff <= 0) {
            clearInterval(self._timer);
            if (self.getAttribute('data-end') === 'hide') {
              var section = self.closest('.shopify-section') || self;
              section.style.display = 'none';
            } else {
              var msg = self.querySelector('[data-cd-message]');
              if (msg) msg.hidden = false;
            }
            diff = 0;
          }
          var secs = Math.floor(diff / 1000);
          setUnit(els.d, Math.floor(secs / 86400));
          setUnit(els.h, Math.floor(secs % 86400 / 3600));
          setUnit(els.m, Math.floor(secs % 3600 / 60));
          setUnit(els.s, secs % 60);
        }
        tick();
        this._timer = setInterval(tick, 1000);
      }
      disconnectedCallback() { clearInterval(this._timer); }
    });
  }

  /* ----------------------------------------------------------------------
     <quantity-input> — button[name=minus] input button[name=plus]
  ---------------------------------------------------------------------- */
  if (!customElements.get('quantity-input')) {
    customElements.define('quantity-input', class extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input');
        var self = this;
        this.querySelectorAll('button').forEach(function (btn) {
          btn.addEventListener('click', function (e) {
            e.preventDefault();
            var v = parseInt(self.input.value, 10) || 0;
            var min = parseInt(self.input.min, 10) || 0;
            v = btn.name === 'plus' ? v + 1 : Math.max(min, v - 1);
            self.input.value = v;
            self.input.dispatchEvent(new Event('change', { bubbles: true }));
          });
        });
      }
    });
  }

  /* ----------------------------------------------------------------------
     Scroll reveal + stagger
  ---------------------------------------------------------------------- */
  function initReveal() {
    // Any bail-out leaves elements fully visible — content is never hidden
    // unless this engine is actively running (protects no-JS visitors and
    // screenshot contexts like the editor's section-picker preview).
    if (document.body.getAttribute('data-animations') === 'false') return;
    if (reducedMotion || !('IntersectionObserver' in window)) return;

    document.querySelectorAll('[data-reveal-stagger]').forEach(function (wrap) {
      Array.prototype.forEach.call(wrap.querySelectorAll('.reveal'), function (el, i) {
        if (!el.style.transitionDelay) el.style.transitionDelay = (i * 80) + 'ms';
      });
    });

    var io = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          obs.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

    function observe(el) {
      el.classList.add('reveal--pending');
      io.observe(el);
    }
    document.querySelectorAll('.reveal').forEach(observe);

    // Sections added later in the editor
    document.addEventListener('shopify:section:load', function (e) {
      e.target.querySelectorAll('.reveal').forEach(observe);
    });
  }

  /* ----------------------------------------------------------------------
     Header: mobile nav + sticky shadow
  ---------------------------------------------------------------------- */
  function initHeader() {
    var toggle = document.querySelector('.header__nav-toggle');
    var nav = document.querySelector('.header__nav');
    if (toggle && nav) {
      var setNav = function (open) {
        nav.classList.toggle('is-open', open);
        toggle.setAttribute('aria-expanded', String(open));
        document.body.classList.toggle('nav-open', open); // lock page scroll
      };
      toggle.addEventListener('click', function () {
        setNav(!nav.classList.contains('is-open'));
      });
      theme.utils.onEscape(function () {
        if (nav.classList.contains('is-open')) {
          setNav(false);
          toggle.focus();
        }
      });
      /* Close (and release the scroll lock) when resizing up to desktop. */
      var deskMQ = window.matchMedia('(min-width: 990px)');
      var onDesk = function (e) { if (e.matches) setNav(false); };
      if (deskMQ.addEventListener) deskMQ.addEventListener('change', onDesk);
      else if (deskMQ.addListener) deskMQ.addListener(onDesk);
    }
    var header = document.querySelector('.header[data-sticky="true"]');
    if (header) {
      var onScroll = function () { header.classList.toggle('is-scrolled', window.scrollY > 4); };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }
  }

  /* ----------------------------------------------------------------------
     Announcement bar rotation
  ---------------------------------------------------------------------- */
  function initAnnouncementBar() {
    var bar = document.querySelector('[data-announcement-bar]');
    if (!bar) return;
    var items = bar.querySelectorAll('[data-announcement-item]');
    if (items.length < 2) return;
    var speed = parseInt(bar.getAttribute('data-rotate'), 10) || 5;
    var i = 0;
    setInterval(function () {
      items[i].classList.remove('is-active');
      i = (i + 1) % items.length;
      items[i].classList.add('is-active');
    }, speed * 1000);
  }

  /* ----------------------------------------------------------------------
     Product gallery (basic thumb swap — full gallery lives in product.js)
  ---------------------------------------------------------------------- */
  function initProductGallery() {
    var gallery = document.querySelector('[data-product-gallery]');
    if (!gallery) return;
    var main = gallery.querySelector('[data-gallery-main] img');
    if (!main) return;
    gallery.querySelectorAll('[data-gallery-thumb]').forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var full = thumb.getAttribute('data-full-src');
        if (full) main.src = full;
        var img = thumb.querySelector('img');
        if (img) main.alt = img.alt;
      });
    });
  }

  /* ----------------------------------------------------------------------
     Scroll parallax — [data-parallax-scroll] (value = speed, default 0.15).
     Transform-only, rAF-batched, passive. Off for reduced-motion / disabled.
  ---------------------------------------------------------------------- */
  function initParallax() {
    if (document.body.getAttribute('data-animations') === 'false' || reducedMotion) return;
    var els = Array.prototype.slice.call(document.querySelectorAll('[data-parallax-scroll]'));
    if (!els.length) return;

    var ticking = false;
    function update() {
      ticking = false;
      var vh = window.innerHeight;
      els.forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (r.bottom < -vh || r.top > vh * 2) return; // skip far off-screen
        var speed = parseFloat(el.getAttribute('data-parallax-scroll')) || 0.15;
        var center = r.top + r.height / 2;
        var offset = (center - vh / 2) * -speed;
        el.style.setProperty('--parallax-y', offset.toFixed(1) + 'px');
      });
    }
    function onScroll() { if (!ticking) { ticking = true; window.requestAnimationFrame(update); } }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  /* ----------------------------------------------------------------------
     Count-up — [data-count-to="1234"] (+ optional -prefix / -suffix).
     Counts when it scrolls into view; reduced-motion shows the final value.
  ---------------------------------------------------------------------- */
  function initCounters() {
    if (document.body.getAttribute('data-animations') === 'false') return;
    var els = document.querySelectorAll('[data-count-to]');
    if (!els.length) return;

    function render(el, value) {
      el.textContent = (el.getAttribute('data-count-prefix') || '') +
        Math.round(value).toLocaleString() +
        (el.getAttribute('data-count-suffix') || '');
    }
    if (reducedMotion || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { render(el, parseFloat(el.getAttribute('data-count-to')) || 0); });
      return;
    }
    function run(el) {
      var to = parseFloat(el.getAttribute('data-count-to')) || 0;
      var dur = 1200;
      var start = null;
      function step(ts) {
        if (start === null) start = ts;
        var p = Math.min((ts - start) / dur, 1);
        render(el, to * (1 - Math.pow(1 - p, 3))); // easeOutCubic
        if (p < 1) window.requestAnimationFrame(step);
      }
      window.requestAnimationFrame(step);
    }
    var io = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        obs.unobserve(entry.target);
        run(entry.target);
      });
    }, { threshold: 0.4 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ----------------------------------------------------------------------
     Card loading skeleton — shimmer behind each product image until it loads
     (CSS handles the shimmer; this just flips .is-loaded on the media).
  ---------------------------------------------------------------------- */
  function initCardSkeleton() {
    function scan(root) {
      (root || document).querySelectorAll('.product-card__media').forEach(function (media) {
        var img = media.querySelector('.product-card__image--primary');
        if (!img || (img.complete && img.naturalWidth > 0)) { media.classList.add('is-loaded'); return; }
        var done = function () { media.classList.add('is-loaded'); };
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
      });
    }
    scan(document);
    document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
  }

  /* ----------------------------------------------------------------------
     Back-to-top — appears after scrolling down; smooth-scrolls to the top.
  ---------------------------------------------------------------------- */
  function initBackToTop() {
    var btn = document.querySelector('[data-back-to-top]');
    if (!btn) return;
    var ticking = false;
    function update() { ticking = false; btn.classList.toggle('is-visible', window.scrollY > 600); }
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }, { passive: true });
    btn.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: reducedMotion ? 'auto' : 'smooth' });
    });
    update();
  }

  /* ----------------------------------------------------------------------
     Reading progress — thin bar tracking how far the page is scrolled.
  ---------------------------------------------------------------------- */
  function initReadingProgress() {
    var host = document.querySelector('[data-reading-progress]');
    if (!host) return;
    var bar = host.querySelector('.reading-progress__bar');
    if (!bar) return;
    var ticking = false;
    function update() {
      ticking = false;
      var h = document.documentElement.scrollHeight - window.innerHeight;
      var pct = h > 0 ? (window.scrollY / h) * 100 : 0;
      bar.style.width = pct + '%';
    }
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  }

  /* ----------------------------------------------------------------------
     Micro-interactions — button ripple + magnetic CTAs. Opt-in via the
     "Premium micro-interactions" setting; fully disabled under reduced motion.
  ---------------------------------------------------------------------- */
  function initMicroInteractions() {
    if (!document.body || document.body.dataset.microInteractions !== 'true') return;
    if (reducedMotion) return;

    // Ripple on any .btn
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.btn');
      if (!btn || btn.hasAttribute('data-no-ripple')) return;
      var rect = btn.getBoundingClientRect();
      var size = Math.max(rect.width, rect.height);
      var ripple = document.createElement('span');
      ripple.className = 'btn-ripple';
      ripple.style.width = ripple.style.height = size + 'px';
      ripple.style.left = (e.clientX - rect.left - size / 2) + 'px';
      ripple.style.top = (e.clientY - rect.top - size / 2) + 'px';
      btn.appendChild(ripple);
      setTimeout(function () { ripple.remove(); }, 600);
    });

    // Magnetic pull on [data-magnetic] elements (fine pointers only)
    if (window.matchMedia && window.matchMedia('(pointer: fine)').matches) {
      document.querySelectorAll('[data-magnetic]').forEach(function (el) {
        el.addEventListener('mousemove', function (e) {
          var r = el.getBoundingClientRect();
          var mx = e.clientX - r.left - r.width / 2;
          var my = e.clientY - r.top - r.height / 2;
          el.style.transform = 'translate(' + (mx * 0.18).toFixed(1) + 'px,' + (my * 0.28).toFixed(1) + 'px)';
        });
        el.addEventListener('mouseleave', function () { el.style.transform = ''; });
      });
    }
  }

  function init() {
    initReveal();
    initHeader();
    initAnnouncementBar();
    initProductGallery();
    initParallax();
    initCounters();
    initCardSkeleton();
    initBackToTop();
    initReadingProgress();
    initMicroInteractions();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
